import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from './config';

// Auth state change listener
export const onAuthStateChange = (callback) => {
  if (!auth) {
    console.warn('Firebase Auth not configured. Using mock auth state.');
    // Return a mock unsubscribe function
    return () => {};
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log('🔍 Firebase Auth: Setting up auth state listener');
  }
  return onAuthStateChanged(auth, (user) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Firebase Auth: Auth state changed in onAuthStateChange:', user);
    }
    callback(user);
  });
};

// Sign in with email and password
export const signIn = async (email, password) => {
  try {
    if (!auth) {
      console.warn('Firebase Auth not configured. Using mock sign in.');
      // Mock successful sign in for development
      return { 
        success: true, 
        user: { 
          uid: 'mock-user-id', 
          email: email,
          displayName: email.split('@')[0],
          emailVerified: true
        } 
      };
    }

    console.log('🔍 Firebase Auth: Attempting sign in for:', email);
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    console.log('✅ Firebase Auth: Sign in successful:', {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified
    });
    
    return { 
      success: true, 
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified
      }
    };
  } catch (error) {
    console.error('❌ Firebase Auth: Sign in failed:', error);
    
    let errorMessage = 'An error occurred during sign in';
    
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No user found with this email address';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  }
};

// Sign up with email and password
export const signUp = async (email, password, displayName = null, emailMarketingOptIn = false) => {
  try {
    if (!auth) {
      console.warn('Firebase Auth not configured. Using mock sign up.');
      // Mock successful sign up for development
      return { 
        success: true, 
        user: { 
          uid: 'mock-user-id-' + Date.now(), 
          email: email,
          displayName: displayName || email.split('@')[0],
          emailVerified: false
        },
        emailMarketingOptIn
      };
    }

    console.log('🔍 Firebase Auth: Attempting sign up for:', email);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update display name if provided
    if (displayName) {
      await updateProfile(user, { displayName });
    }
    
    // Send email verification with custom action code settings
    try {
      // Get the current URL to determine if we're in production or development
      const isProduction = typeof window !== 'undefined' && 
        (window.location.hostname.includes('gearcompare-49e60.web.app') || 
         window.location.hostname.includes('gearcompare-49e60.firebaseapp.com'));
      
      const continueUrl = isProduction 
        ? 'https://gearcompare-49e60.web.app/login?verified=true'
        : `${window.location.origin}/login?verified=true`;
      
      const actionCodeSettings = {
        url: continueUrl,
        handleCodeInApp: false, // Open link in browser, not in-app
      };
      
      await sendEmailVerification(user, actionCodeSettings);
      console.log('✅ Firebase Auth: Email verification sent');
    } catch (verificationError) {
      console.warn('⚠️ Firebase Auth: Could not send email verification:', verificationError);
    }
    
    console.log('✅ Firebase Auth: Sign up successful:', {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified,
      emailMarketingOptIn
    });
    
    return { 
      success: true, 
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified
      },
      emailMarketingOptIn
    };
  } catch (error) {
    console.error('❌ Firebase Auth: Sign up failed:', error);
    
    let errorMessage = 'An error occurred during sign up';
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'An account with this email already exists';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'Email/password accounts are not enabled';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  }
};

// Sign out
export const signOutUser = async () => {
  try {
    if (!auth) {
      console.warn('Firebase Auth not configured. Using mock sign out.');
      return { success: true };
    }

    console.log('🔍 Firebase Auth: Attempting sign out');
    await firebaseSignOut(auth);
    console.log('✅ Firebase Auth: Sign out successful');
    
    return { success: true };
  } catch (error) {
    console.error('❌ Firebase Auth: Sign out failed:', error);
    return { success: false, error: error.message };
  }
};

// Send password reset email
export const resetPassword = async (email) => {
  try {
    if (!auth) {
      console.warn('Firebase Auth not configured. Using mock password reset.');
      return { success: true };
    }

    console.log('🔍 Firebase Auth: Sending password reset email to:', email);
    await sendPasswordResetEmail(auth, email);
    console.log('✅ Firebase Auth: Password reset email sent');
    
    return { success: true };
  } catch (error) {
    console.error('❌ Firebase Auth: Password reset failed:', error);
    
    let errorMessage = 'An error occurred while sending password reset email';
    
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No user found with this email address';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Invalid email address';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many requests. Please try again later';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  }
};

// Get current user
export const getCurrentUser = () => {
  if (!auth) {
    console.warn('Firebase Auth not configured. No current user.');
    return null;
  }
  
  return auth.currentUser;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const user = getCurrentUser();
  return user !== null;
};

// Get user ID token
export const getIdToken = async (forceRefresh = false) => {
  try {
    const user = getCurrentUser();
    if (!user) {
      return null;
    }
    
    return await user.getIdToken(forceRefresh);
  } catch (error) {
    console.error('❌ Firebase Auth: Error getting ID token:', error);
    return null;
  }
};

// Sign in with Google
export const signInWithGoogle = async () => {
  try {
    if (!auth) {
      console.warn('Firebase Auth not configured. Using mock Google sign in.');
      return { 
        success: true, 
        user: { 
          uid: 'mock-google-user-id', 
          email: 'mock@google.com',
          displayName: 'Mock Google User',
          emailVerified: true
        } 
      };
    }

    console.log('🔍 Firebase Auth: Attempting Google sign in');
    const provider = new GoogleAuthProvider();
    // Request additional scopes if needed
    provider.addScope('profile');
    provider.addScope('email');
    
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;
    
    console.log('✅ Firebase Auth: Google sign in successful:', {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      emailVerified: user.emailVerified
    });
    
    return { 
      success: true, 
      user: {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified
      }
    };
  } catch (error) {
    console.error('❌ Firebase Auth: Google sign in failed:', error);
    
    let errorMessage = 'An error occurred during Google sign in';
    
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = 'Sign in was cancelled';
        break;
      case 'auth/popup-blocked':
        errorMessage = 'Popup was blocked. Please allow popups and try again';
        break;
      case 'auth/cancelled-popup-request':
        errorMessage = 'Only one popup request is allowed at a time';
        break;
      case 'auth/account-exists-with-different-credential':
        errorMessage = 'An account already exists with the same email address but different sign-in credentials';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  }
};

// Send email verification
export const sendEmailVerificationEmail = async (user) => {
  try {
    if (!auth) {
      console.warn('Firebase Auth not configured. Cannot send email verification.');
      return { success: false, error: 'Firebase Auth not configured' };
    }

    if (!user) {
      user = getCurrentUser();
      if (!user) {
        return { success: false, error: 'No user found' };
      }
    }

    console.log('🔍 Firebase Auth: Sending email verification to:', user.email);
    
    // Get the current URL to determine if we're in production or development
    const isProduction = typeof window !== 'undefined' && 
      (window.location.hostname.includes('gearcompare-49e60.web.app') || 
       window.location.hostname.includes('gearcompare-49e60.firebaseapp.com'));
    
    const continueUrl = isProduction 
      ? 'https://gearcompare-49e60.web.app/login?verified=true'
      : `${window.location.origin}/login?verified=true`;
    
    const actionCodeSettings = {
      url: continueUrl,
      handleCodeInApp: false, // Open link in browser, not in-app
    };
    
    await sendEmailVerification(user, actionCodeSettings);
    console.log('✅ Firebase Auth: Email verification sent');
    
    return { success: true };
  } catch (error) {
    console.error('❌ Firebase Auth: Email verification failed:', error);
    
    let errorMessage = 'An error occurred while sending verification email';
    
    switch (error.code) {
      case 'auth/too-many-requests':
        errorMessage = 'Too many requests. Please try again later';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Network error. Please check your connection';
        break;
      default:
        errorMessage = error.message;
    }
    
    return { success: false, error: errorMessage };
  }
};